import { add, differenceInHours, endOfHour, startOfHour } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import { head, last } from 'lodash';
import { DateRange } from 'react-day-picker';

import { roundToHours } from '@/components/route-segment-panel/utils';
import { MODE_WATER } from '@/lib/constants';
import { RouteScheduleInterface } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { calculateScheduleTimelineBars } from '@/utils/schedules';

interface GetScheduleChartArgs {
  schedules: RouteScheduleInterface[];
  mode: SegmentModeType;
  customDateRange?: DateRange;
  isCompactView?: boolean;
}

interface ScheduleChartData {
  shouldShowTimezones: boolean;
  rangeStartTime: Date;
  timeRangeHours: number;
  bars: Date[];
}

const ROUNDED_HOURS_BASE = 6;

const getRoundedHoursBetween = (start: Date, end: Date) => {
  const hoursBetween = differenceInHours(end, start);
  return hoursBetween < 0 ? ROUNDED_HOURS_BASE : Math.ceil(hoursBetween / ROUNDED_HOURS_BASE) * ROUNDED_HOURS_BASE;
};

export const getScheduleChartData = ({
  schedules,
  mode,
  customDateRange,
  isCompactView = false,
}: GetScheduleChartArgs): ScheduleChartData => {
  const isOcean = mode === MODE_WATER;

  const schedulesByDepartureTime = [...schedules].sort((a, b) => {
    return a.departureDateTime.valueOf() - b.departureDateTime.valueOf();
  });

  const schedulesByArrivalTime = [...schedules].sort((a, b) => {
    return a.arrivalDateTime.valueOf() - b.arrivalDateTime.valueOf();
  });

  const firstDepartureSchedule = head(schedulesByDepartureTime);
  const origin = firstDepartureSchedule?.from;
  const destination = firstDepartureSchedule?.to;

  if (!origin || !destination) throw new Error('Missing origin or destination location');

  const hasDifferentTimezones = getTimezoneOffset(origin.timezoneName) !== getTimezoneOffset(destination.timezoneName);
  const shouldShowTimezones = hasDifferentTimezones && !isOcean;

  if (customDateRange?.to && customDateRange?.from) {
    const { from, to } = customDateRange;
    const timeRange = getRoundedHoursBetween(from, to);
    const { bars, timeRangeHours } = calculateScheduleTimelineBars(timeRange, from, isCompactView);

    return {
      shouldShowTimezones,
      rangeStartTime: from,
      timeRangeHours,
      bars,
    };
  }

  // For first departure/arrival we check to see if a scaleOverride value was
  // given. This is used for situations like ocean search to ensure the chart
  // starts at the date given in the search form
  const firstDeparture = customDateRange?.from ? customDateRange.from : firstDepartureSchedule.departureDateTime;
  const firstDepartureRounded = roundToHours(startOfHour(firstDeparture), 3);
  const rangeStartTime = roundToHours(startOfHour(firstDeparture), 3);

  const lastArrival = customDateRange?.to ? customDateRange.to : last(schedulesByArrivalTime)!.arrivalDateTime;
  const lastArrivalRounded = add(endOfHour(lastArrival), { hours: 1 });
  const timeRange = getRoundedHoursBetween(firstDepartureRounded, lastArrivalRounded);
  const { bars, timeRangeHours } = calculateScheduleTimelineBars(timeRange, rangeStartTime, isCompactView);

  return {
    shouldShowTimezones,
    rangeStartTime,
    timeRangeHours,
    bars,
  };
};
